import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Lock } from "lucide-react";
import { motion } from "framer-motion";
import WeaverLogo from "./WeaverLogo";

const CLIENT_PAYMENT_URL = "https://square.link/u/NuVJwHUZ";

const PaymentOptions = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="payment-page">
      {/* Minimal header */}
      <header className="w-full px-6 sm:px-12 py-6 sm:py-8 flex items-center justify-between border-b border-white/5">
        <Link to="/" data-testid="payment-back-link" className="focus:outline-none">
          <WeaverLogo size="md" data-testid="payment-page-logo" />
        </Link>
        <Link
          to="/"
          data-testid="payment-back-home"
          className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-zinc-400 hover:text-white transition-colors duration-300"
        >
          <ArrowLeft
            size={14}
            className="transition-transform duration-500 group-hover:-translate-x-1"
          />
          Back
        </Link>
      </header>

      <main className="flex-grow flex items-center justify-center px-6 sm:px-12 py-20 sm:py-28">
        <div className="w-full max-w-2xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="block text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-6"
          >
            — Secure Checkout
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95] text-white"
            data-testid="payment-page-title"
          >
            Payment
            <br />
            <span className="text-zinc-500">Options</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 text-base sm:text-lg font-light text-zinc-300 leading-relaxed tracking-wide max-w-xl mx-auto"
            data-testid="payment-page-blurb"
          >
            Thank you for letting{" "}
            <span className="text-white font-medium">Weaver Media</span> be a
            part of your vision. To pay an invoice using a credit card, we
            offer payment options using{" "}
            <span className="text-white font-medium">SQUARE</span> payments.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14"
          >
            <div
              className="border border-white/10 bg-[#0A0A0A] p-8 sm:p-12"
              data-testid="payment-square-card"
            >
              <div className="flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-6">
                <Lock size={12} />
                <span>SSL Secured · Powered by Square</span>
              </div>

              <p className="text-sm sm:text-base font-light text-zinc-400 leading-relaxed">
                You&apos;ll be redirected to our secure Square checkout to
                complete your payment. Square accepts all major credit and debit
                cards.
              </p>

              <a
                href={CLIENT_PAYMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="payment-square-link"
                className="mt-10 group inline-flex items-center justify-center gap-3 w-full px-10 py-5 bg-white text-black font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-zinc-200 transition-all duration-300"
              >
                Open Square Checkout
                <ArrowUpRight
                  size={18}
                  className="transition-transform duration-500 group-hover:rotate-45"
                />
              </a>

              <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-600">
                Opens in a new tab · square.link
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-16 text-xs font-light text-zinc-500 leading-relaxed"
          >
            Questions about your invoice?{" "}
            <Link
              to="/?contact=1"
              data-testid="payment-contact-link"
              className="text-white underline-offset-4 hover:underline"
            >
              Get in touch
            </Link>
            .
          </motion.div>
        </div>
      </main>

      <footer className="w-full px-6 sm:px-12 py-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-600">
          © 2026 by Weaver Media
        </p>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-600">
          Remote · Worldwide
        </p>
      </footer>
    </div>
  );
};

export default PaymentOptions;
